<template>
  <transition :name="isMobile ? '' : 'fade'">
    <div v-if="open"
         class="popover-modal modal-post"
         :style="isMobile ? mobileInlineStyles : {}">
      <div class="modal-shadow" @click="close" style="z-index: 0 !important; pointer-events: auto !important;"/>
      <div class="content-box bg-white rounded-sm" :style="isMobile ? mobileContentStyles : {}" ref="contentBox" style="z-index: 1 !important; position: relative !important;" @click.stop>
        <header class="flex h-base items-center pl-base">
          <button v-if="allowCoBack" class="w-base h-base ml-auto" @click="goBack">
            <i class="material-icons">arrow_back</i>
          </button>
          <slot name="header" />
          <button class="w-base h-base ml-auto" @click="close">
            <i class="material-icons">close</i>
          </button>
        </header>
        <slot/>
      </div>
    </div>
  </transition>
</template>

<script>
import { getRootPath } from '../../../lib/bookmarks'
import { bodyScrollGuard } from '../../../lib/control-body-scroll'
import clickOutside from 'vue-click-outside'

export default {
  name: 'PopoverModalPost',
  directives: { clickOutside },
  props: {
    bookmark: null
  },
  data: () => ({
    allowClickOutside: true,
    allowCoBack: true,
    open: true,
    isMobile: false,
    mobileInlineStyles: {},
    mobileContentStyles: {}
  }),

  created () {
    // Check if mobile immediately
    if (typeof window !== 'undefined') {
      this.updateMobileState()
      console.log('[PopoverModalPost] Created, isMobile:', this.isMobile, 'window.innerWidth:', window.innerWidth, 'route:', this.$route ? this.$route.name : 'no route')
    }
    // Ensure popup is open when component is created (for router navigation)
    this.open = true
    console.log('[PopoverModalPost] Created, setting open to true, route name:', this.$route ? this.$route.name : 'no route')
    window.addEventListener('keydown', e => {
      if (e.key === 'Escape' || e.keyCode === 27) {
        this.$emit('esc', e)
        this.close()
      }
    })
    // Listen for resize to update mobile state with debounce
    if (typeof window !== 'undefined') {
      let resizeTimeout
      this.handleResize = () => {
        clearTimeout(resizeTimeout)
        resizeTimeout = setTimeout(() => {
          const wasMobile = this.isMobile
          this.updateMobileState()
          // If state changed, apply styles immediately
          if (wasMobile !== this.isMobile) {
            this.$nextTick(() => {
              this.applyMobileStyles()
            })
          }
        }, 100)
      }
      window.addEventListener('resize', this.handleResize)
    }
  },

  mounted () {
    // Ensure popup is open when mounted (for router navigation)
    if (this.$route && (this.$route.name === 'event' || this.$route.name === 'author-blog' || this.$route.name === 'profile')) {
      this.open = true
      console.log('[PopoverModalPost] Mounted, route is event/blog/profile, setting open to true')
    }
    console.log('[PopoverModalPost] Mounted, open:', this.open, 'isMobile:', this.isMobile, 'route:', this.$route ? this.$route.name : 'no route')
    this.setBodyScroll(this.open)
    this.setAllowClickOutside()
    // Ensure mobile state is updated and styles are applied
    this.updateMobileState()
    // Scroll window to top to show popup immediately
    if (this.open) {
      window.scrollTo(0, 0) // Immediate scroll first
      if (this.isMobile && this.$refs.contentBox) {
        this.$refs.contentBox.scrollTop = 0
      }
    }
    // Apply styles after DOM is ready - use multiple ticks to ensure content is loaded
    this.$nextTick(() => {
      console.log('[PopoverModalPost] Applying mobile styles in nextTick, isMobile:', this.isMobile, 'open:', this.open)
      this.applyMobileStyles()
      // Force scroll to top on mobile when mounted
      if (this.isMobile) {
        window.scrollTo(0, 0) // Immediate scroll
        if (this.$refs.contentBox) {
          this.$refs.contentBox.scrollTop = 0
        }
        // Also scroll smoothly after immediate scroll
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }, 50)
      } else if (!this.isMobile && this.open && this.$el) {
        // On desktop, scroll to show popup
        const modal = this.$el.querySelector('.popover-modal.modal-post')
        if (modal) {
          // For fixed position modals, we need to scroll the window to where the modal would be
          // Get the modal's position relative to document
          const rect = modal.getBoundingClientRect()
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop
          const modalTop = rect.top + scrollTop
          // Scroll to center the modal in viewport
          const viewportHeight = window.innerHeight
          const targetScroll = Math.max(0, modalTop - (viewportHeight / 2) + (rect.height / 2))
          // Immediate scroll first
          window.scrollTo(0, targetScroll)
          // Then smooth scroll after a short delay
          setTimeout(() => {
            window.scrollTo({ top: targetScroll, behavior: 'smooth' })
          }, 50)
        }
      }
      // Also apply after a short delay to ensure nested content is rendered
      setTimeout(() => {
        this.applyMobileStyles()
        if (this.isMobile) {
          window.scrollTo(0, 0)
          if (this.$refs.contentBox) {
            this.$refs.contentBox.scrollTop = 0
          }
        } else if (!this.isMobile && this.open && this.$el) {
          // Retry desktop scroll after content loads
          const modal = this.$el.querySelector('.popover-modal.modal-post')
          if (modal) {
            const rect = modal.getBoundingClientRect()
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop
            const modalTop = rect.top + scrollTop
            const viewportHeight = window.innerHeight
            const targetScroll = Math.max(0, modalTop - (viewportHeight / 2) + (rect.height / 2))
            window.scrollTo({ top: targetScroll, behavior: 'smooth' })
          }
        }
      }, 100)
      // Additional scroll after longer delay to catch async content
      setTimeout(() => {
        this.applyMobileStyles()
        if (this.isMobile) {
          window.scrollTo(0, 0)
          if (this.$refs.contentBox) {
            this.$refs.contentBox.scrollTop = 0
          }
        }
      }, 500)
    })
  },

  watch: {
    '$route' () {
      // Ensure popup is open when route changes (for router navigation)
      if (this.$route.name === 'event' || this.$route.name === 'author-blog' || this.$route.name === 'profile') {
        this.open = true
        this.$nextTick(() => {
          this.updateMobileState()
          this.applyMobileStyles()
        })
      }
    },
    open (value) {
      this.setBodyScroll(value)
      this.setAllowClickOutside()
      // When modal opens, ensure mobile styles are applied
      if (value) {
        // Immediate scroll first (especially important on mobile)
        window.scrollTo(0, 0)
        if (this.isMobile && this.$refs.contentBox) {
          this.$refs.contentBox.scrollTop = 0
        }
        this.$nextTick(() => {
          this.updateMobileState()
          this.applyMobileStyles()
          // Force scroll to top when opening on mobile
          if (this.isMobile) {
            window.scrollTo(0, 0) // Immediate scroll
            if (this.$refs.contentBox) {
              this.$refs.contentBox.scrollTop = 0
            }
            // Also scroll smoothly after immediate scroll
            setTimeout(() => {
              window.scrollTo({ top: 0, behavior: 'smooth' })
            }, 50)
          } else if (!this.isMobile && this.$el) {
            // On desktop, ensure popup is visible by scrolling to it
            const modal = this.$el.querySelector('.popover-modal.modal-post')
            if (modal) {
              // For fixed position modals, we need to scroll the window to where the modal would be
              const rect = modal.getBoundingClientRect()
              const scrollTop = window.pageYOffset || document.documentElement.scrollTop
              const modalTop = rect.top + scrollTop
              // Scroll to center the modal in viewport
              const viewportHeight = window.innerHeight
              const targetScroll = Math.max(0, modalTop - (viewportHeight / 2) + (rect.height / 2))
              // Immediate scroll first
              window.scrollTo(0, targetScroll)
              // Then smooth scroll after a short delay
              setTimeout(() => {
                window.scrollTo({ top: targetScroll, behavior: 'smooth' })
              }, 50)
            }
          }
          // Also apply after content loads - use multiple timeouts to catch async content
          setTimeout(() => {
            this.applyMobileStyles()
            if (this.isMobile) {
              window.scrollTo(0, 0)
              if (this.$refs.contentBox) {
                this.$refs.contentBox.scrollTop = 0
              }
            } else if (!this.isMobile && this.open && this.$el) {
              // Retry desktop scroll after content loads
              const modal = this.$el.querySelector('.popover-modal.modal-post')
              if (modal) {
                const rect = modal.getBoundingClientRect()
                const scrollTop = window.pageYOffset || document.documentElement.scrollTop
                const modalTop = rect.top + scrollTop
                const viewportHeight = window.innerHeight
                const targetScroll = Math.max(0, modalTop - (viewportHeight / 2) + (rect.height / 2))
                window.scrollTo({ top: targetScroll, behavior: 'smooth' })
              }
            }
          }, 100)
          setTimeout(() => {
            this.applyMobileStyles()
            if (this.isMobile) {
              window.scrollTo(0, 0)
              if (this.$refs.contentBox) {
                this.$refs.contentBox.scrollTop = 0
              }
            }
          }, 300)
          setTimeout(() => {
            this.applyMobileStyles()
            if (this.isMobile) {
              window.scrollTo(0, 0)
              if (this.$refs.contentBox) {
                this.$refs.contentBox.scrollTop = 0
              }
            }
          }, 600)
        })
      }
    },
    isMobile (newVal, oldVal) {
      // When mobile state changes, ensure styles are applied
      if (newVal !== oldVal) {
        this.$nextTick(() => {
          this.applyMobileStyles()
        })
      }
    }
  },

  methods: {
    applyMobileStyles () {
      if (!this.$el) {
        console.warn('[PopoverModalPost] applyMobileStyles: $el is not available')
        return
      }
      const modal = this.$el.querySelector('.popover-modal.modal-post')
      const contentBox = this.$el.querySelector('.content-box')
      const modalShadow = this.$el.querySelector('.modal-shadow')

      if (!modal) {
        console.warn('[PopoverModalPost] applyMobileStyles: modal element not found')
      }
      if (!contentBox) {
        console.warn('[PopoverModalPost] applyMobileStyles: contentBox element not found')
      }

      if (this.isMobile) {
        console.log('[PopoverModalPost] Applying mobile styles, isMobile:', this.isMobile)
        if (modal) {
          modal.style.setProperty('width', '100vw', 'important')
          modal.style.setProperty('max-width', '100vw', 'important')
          modal.style.setProperty('height', '100vh', 'important')
          modal.style.setProperty('top', '0', 'important')
          modal.style.setProperty('left', '0', 'important')
          modal.style.setProperty('right', '0', 'important')
          modal.style.setProperty('bottom', '0', 'important')
          modal.style.setProperty('transform', 'none', 'important')
          modal.style.setProperty('position', 'fixed', 'important')
          modal.style.setProperty('margin', '0', 'important')
          modal.style.setProperty('padding', '0', 'important')
          modal.style.setProperty('transition', 'none', 'important')
          modal.style.setProperty('background', 'transparent', 'important')
        }
        if (modalShadow) {
          modalShadow.style.setProperty('width', '100vw', 'important')
          modalShadow.style.setProperty('height', '100vh', 'important')
          modalShadow.style.setProperty('left', '0', 'important')
          modalShadow.style.setProperty('top', '0', 'important')
          modalShadow.style.setProperty('right', '0', 'important')
          modalShadow.style.setProperty('bottom', '0', 'important')
          modalShadow.style.setProperty('transform', 'none', 'important')
          modalShadow.style.setProperty('position', 'fixed', 'important')
          modalShadow.style.setProperty('z-index', '0', 'important')
          modalShadow.style.setProperty('background', 'rgba(215, 215, 215, 0.8)', 'important')
          modalShadow.style.setProperty('pointer-events', 'auto', 'important')
          modalShadow.style.setProperty('cursor', 'pointer', 'important')
        }
        if (contentBox) {
          contentBox.style.setProperty('max-height', '100vh', 'important')
          contentBox.style.setProperty('height', '100vh', 'important')
          contentBox.style.setProperty('width', '100%', 'important')
          contentBox.style.setProperty('max-width', '100%', 'important')
          // Ensure scrolling is enabled
          contentBox.style.setProperty('overflow', 'auto', 'important')
          contentBox.style.setProperty('overflow-y', 'auto', 'important')
          contentBox.style.setProperty('overflow-x', 'hidden', 'important')
          contentBox.style.setProperty('-webkit-overflow-scrolling', 'touch', 'important')
          contentBox.style.setProperty('touch-action', 'pan-y', 'important')
          contentBox.style.setProperty('overscroll-behavior', 'contain', 'important')
          contentBox.style.setProperty('display', 'block', 'important')
          contentBox.style.setProperty('position', 'relative', 'important')
          contentBox.style.setProperty('top', '0', 'important')
          contentBox.style.setProperty('left', '0', 'important')
          contentBox.style.setProperty('right', 'auto', 'important')
          contentBox.style.setProperty('bottom', 'auto', 'important')
          contentBox.style.setProperty('transform', 'none', 'important')
          contentBox.style.setProperty('margin', '0', 'important')
          contentBox.style.setProperty('z-index', '1', 'important')
          contentBox.style.setProperty('transition', 'none', 'important')
          contentBox.style.setProperty('min-height', '0', 'important')
          contentBox.style.setProperty('visibility', 'visible', 'important')
          contentBox.style.setProperty('opacity', '1', 'important')
          // Ensure pointer events are enabled for scrolling
          contentBox.style.setProperty('pointer-events', 'auto', 'important')
          // Ensure nested content can flow and be scrollable
          const contentWithSidebar = contentBox.querySelector('.content-with-sidebar')
          if (contentWithSidebar) {
            console.log('[PopoverModalPost] Found content-with-sidebar, applying styles')
            // Force content to expand to its natural height
            contentWithSidebar.style.removeProperty('height')
            contentWithSidebar.style.setProperty('height', 'auto', 'important')
            contentWithSidebar.style.removeProperty('min-height')
            contentWithSidebar.style.setProperty('min-height', 'auto', 'important')
            contentWithSidebar.style.removeProperty('max-height')
            contentWithSidebar.style.setProperty('max-height', 'none', 'important')
            // Remove overflow properties first to avoid conflicts
            contentWithSidebar.style.removeProperty('overflow')
            contentWithSidebar.style.removeProperty('overflow-y')
            contentWithSidebar.style.removeProperty('overflow-x')
            // Then set them in the correct order - content-with-sidebar should not scroll, parent should
            contentWithSidebar.style.setProperty('overflow-x', 'hidden', 'important')
            contentWithSidebar.style.setProperty('overflow-y', 'visible', 'important')
            // Ensure touch events work
            contentWithSidebar.style.setProperty('pointer-events', 'auto', 'important')
            contentWithSidebar.style.setProperty('touch-action', 'auto', 'important')
            contentWithSidebar.style.setProperty('display', 'block', 'important')
            contentWithSidebar.style.setProperty('position', 'relative', 'important')
            contentWithSidebar.style.setProperty('width', '100%', 'important')
            contentWithSidebar.style.setProperty('visibility', 'visible', 'important')
            contentWithSidebar.style.setProperty('opacity', '1', 'important')
            contentWithSidebar.style.setProperty('flex-shrink', '0', 'important')
            contentWithSidebar.style.setProperty('flex-grow', '0', 'important')
            contentWithSidebar.style.setProperty('align-self', 'flex-start', 'important')
            // Force a reflow to measure content
            void contentWithSidebar.offsetHeight
            // Also fix main-box and sidebar-box
            const mainBox = contentWithSidebar.querySelector('.main-box')
            const sidebarBox = contentWithSidebar.querySelector('.sidebar-box')
            if (mainBox) {
              // Force main-box to expand to fit content
              mainBox.style.removeProperty('height')
              mainBox.style.setProperty('height', 'auto', 'important')
              mainBox.style.removeProperty('min-height')
              mainBox.style.setProperty('min-height', 'auto', 'important')
              mainBox.style.removeProperty('max-height')
              mainBox.style.setProperty('max-height', 'none', 'important')
              mainBox.style.setProperty('overflow', 'visible', 'important')
              mainBox.style.setProperty('overflow-y', 'visible', 'important')
              mainBox.style.setProperty('position', 'relative', 'important')
              mainBox.style.setProperty('width', '100%', 'important')
              mainBox.style.setProperty('max-width', '100%', 'important')
              mainBox.style.setProperty('visibility', 'visible', 'important')
              mainBox.style.setProperty('opacity', '1', 'important')
              mainBox.style.setProperty('flex-shrink', '0', 'important')
              mainBox.style.setProperty('flex-grow', '0', 'important')
              mainBox.style.setProperty('align-self', 'flex-start', 'important')
              // Force a reflow to measure content
              void mainBox.offsetHeight
            }
            // Ensure blog articles are visible
            const blogArticles = contentBox.querySelectorAll('.blog-article')
            console.log('[PopoverModalPost] Found blog articles:', blogArticles.length)
            blogArticles.forEach((article, index) => {
              article.style.setProperty('display', 'block', 'important')
              article.style.setProperty('visibility', 'visible', 'important')
              article.style.setProperty('opacity', '1', 'important')
              console.log(`[PopoverModalPost] Applied styles to blog article ${index + 1}`)
            })
            if (sidebarBox) {
              sidebarBox.style.setProperty('height', 'auto', 'important')
              sidebarBox.style.setProperty('min-height', '0', 'important')
              sidebarBox.style.setProperty('max-height', 'none', 'important')
              sidebarBox.style.setProperty('overflow', 'visible', 'important')
              sidebarBox.style.setProperty('overflow-y', 'visible', 'important')
              sidebarBox.style.setProperty('position', 'relative', 'important')
              sidebarBox.style.setProperty('visibility', 'visible', 'important')
              sidebarBox.style.setProperty('opacity', '1', 'important')
            }
          } else {
            console.warn('[PopoverModalPost] content-with-sidebar not found in contentBox')
          }
          // Log content box dimensions for debugging
          if (contentBox) {
            const rect = contentBox.getBoundingClientRect()
            const contentWithSidebar = contentBox.querySelector('.content-with-sidebar')
            const mainBox = contentWithSidebar ? contentWithSidebar.querySelector('.main-box') : null
            const computedStyle = getComputedStyle(contentBox)
            console.log('[PopoverModalPost] ContentBox dimensions:', {
              width: rect.width,
              height: rect.height,
              scrollHeight: contentBox.scrollHeight,
              clientHeight: contentBox.clientHeight,
              top: rect.top,
              left: rect.left,
              contentWithSidebarHeight: contentWithSidebar ? contentWithSidebar.scrollHeight : 'not found',
              mainBoxHeight: mainBox ? mainBox.scrollHeight : 'not found',
              contentBoxComputedStyle: {
                overflowY: computedStyle.overflowY,
                display: computedStyle.display,
                visibility: computedStyle.visibility,
                opacity: computedStyle.opacity,
                position: computedStyle.position,
                top: computedStyle.top,
                transform: computedStyle.transform,
                marginTop: computedStyle.marginTop
              },
              inlineStyles: {
                top: contentBox.style.top,
                transform: contentBox.style.transform,
                position: contentBox.style.position
              }
            })
            // Force reset positioning if it's wrong
            if (rect.top < -100 || rect.top > 100) {
              console.warn('[PopoverModalPost] ContentBox has incorrect top position:', rect.top, 'forcing reset')
              contentBox.style.removeProperty('top')
              contentBox.style.setProperty('top', '0', 'important')
              contentBox.style.removeProperty('transform')
              contentBox.style.setProperty('transform', 'none', 'important')
              contentBox.style.removeProperty('margin-top')
              contentBox.style.setProperty('margin-top', '0', 'important')
              contentBox.style.removeProperty('margin')
              contentBox.style.setProperty('margin', '0', 'important')
              // Also ensure the modal itself is positioned correctly
              if (modal) {
                modal.style.setProperty('top', '0', 'important')
                modal.style.setProperty('left', '0', 'important')
                modal.style.setProperty('transform', 'none', 'important')
              }
              // Force scroll to top
              contentBox.scrollTop = 0
              // Force a reflow
              void contentBox.offsetHeight
            }
          }
          // Force a reflow after applying styles to ensure content is visible
          if (contentBox) {
            void contentBox.offsetHeight
            // Scroll to top to ensure content is visible - do it immediately and with delays
            contentBox.scrollTop = 0
            this.$nextTick(() => {
              contentBox.scrollTop = 0
              // Also try scrolling after multiple delays to ensure it sticks
              setTimeout(() => {
                contentBox.scrollTop = 0
              }, 50)
              setTimeout(() => {
                contentBox.scrollTop = 0
              }, 200)
              setTimeout(() => {
                contentBox.scrollTop = 0
              }, 500)
            })
          }
        }
      } else {
        if (modal) {
          modal.style.removeProperty('width')
          modal.style.removeProperty('max-width')
          modal.style.removeProperty('height')
          modal.style.removeProperty('top')
          modal.style.removeProperty('left')
          modal.style.removeProperty('right')
          modal.style.removeProperty('bottom')
          modal.style.removeProperty('transform')
          modal.style.removeProperty('position')
          modal.style.removeProperty('margin')
          modal.style.removeProperty('padding')
          modal.style.removeProperty('transition')
          modal.style.removeProperty('background')
        }
        if (modalShadow) {
          modalShadow.style.removeProperty('width')
          modalShadow.style.removeProperty('height')
          modalShadow.style.removeProperty('left')
          modalShadow.style.removeProperty('top')
          modalShadow.style.removeProperty('right')
          modalShadow.style.removeProperty('bottom')
          modalShadow.style.removeProperty('transform')
          modalShadow.style.removeProperty('position')
          modalShadow.style.removeProperty('z-index')
          modalShadow.style.removeProperty('background')
        }
        if (contentBox) {
          contentBox.style.removeProperty('max-height')
          contentBox.style.removeProperty('height')
          contentBox.style.removeProperty('width')
          contentBox.style.removeProperty('max-width')
          contentBox.style.removeProperty('overflow-y')
          contentBox.style.removeProperty('overflow-x')
          contentBox.style.removeProperty('-webkit-overflow-scrolling')
          contentBox.style.removeProperty('touch-action')
          contentBox.style.removeProperty('overscroll-behavior')
          contentBox.style.removeProperty('display')
          contentBox.style.removeProperty('position')
          contentBox.style.removeProperty('z-index')
          contentBox.style.removeProperty('transition')
          contentBox.style.removeProperty('min-height')
        }
      }
    },

    updateMobileState () {
      if (typeof window !== 'undefined') {
        const wasMobile = this.isMobile
        const newIsMobile = window.innerWidth <= 480
        console.log('[PopoverModalPost] updateMobileState, window.innerWidth:', window.innerWidth, 'newIsMobile:', newIsMobile, 'wasMobile:', wasMobile)
        this.isMobile = newIsMobile
        if (this.isMobile) {
          this.mobileInlineStyles = {
            width: '100vw',
            maxWidth: '100vw',
            height: '100vh',
            top: '0',
            left: '0',
            right: '0',
            bottom: '0',
            transform: 'none',
            position: 'fixed',
            margin: '0',
            padding: '0',
            transition: 'none'
          }
          this.mobileContentStyles = {
            height: '100vh',
            width: '100%',
            maxWidth: '100%',
            transition: 'none'
          }
        } else {
          this.mobileInlineStyles = {}
          this.mobileContentStyles = {}
        }
        // If mobile state changed, update body scroll
        if (wasMobile !== this.isMobile) {
          this.setBodyScroll(this.open)
        }
      }
    },

    goBack () {
      history.back()
    },

    close () {
      // On mobile, always allow closing
      if (this.open && (this.allowClickOutside || this.isMobile)) {
        console.log('[PopoverModalPost] Closing modal, isMobile:', this.isMobile, 'allowClickOutside:', this.allowClickOutside)
        this.open = false
        this.$emit('close')
        setTimeout(() => {
          const rootPath = getRootPath() || '/'
          this.$router.push(rootPath)
        }, 200)
      } else {
        console.log('[PopoverModalPost] Close blocked, isMobile:', this.isMobile, 'allowClickOutside:', this.allowClickOutside, 'open:', this.open)
      }
    },

    setBodyScroll (freeze) {
      // On mobile, don't freeze body scroll - let the modal handle its own scrolling
      if (window.innerWidth <= 480) {
        // Don't freeze body scroll on mobile to allow modal scrolling
        return
      }
      if (freeze) {
        bodyScrollGuard.freezeBodyScroll()
      } else {
        bodyScrollGuard.releaseBodyScroll()
      }
    },

    releaseBgScroll () {
      this.setBodyScroll(false)
    },

    setAllowClickOutside () {
      if (this.open) {
        // On mobile, allow clicking immediately
        if (this.isMobile) {
          this.allowClickOutside = true
        } else {
          setTimeout(() => { this.allowClickOutside = true }, 100)
        }
      } else {
        this.allowClickOutside = false
      }
    }
  },

  beforeDestroy () {
    if (typeof window !== 'undefined' && this.handleResize) {
      window.removeEventListener('resize', this.handleResize)
    }
    bodyScrollGuard.releaseBodyScroll()
  }
}
</script>

<!--suppress CssInvalidAtRule -->
<style lang="scss">
  @import "../../../styles/vars";
  @import "../../../styles/mixins";

  .popover-modal.modal-post {
    z-index: 99999 !important;
    position: fixed !important;
    // Create new stacking context to ensure it's above everything
    isolation: isolate !important;

    // Ensure PDF modals inside event modals appear on top
    // Note: PDF modal z-index is set in AttachmentsView.vue to 999999
    // This rule ensures it works within event modals
    .pdf-modal.popover-modal {
      z-index: 999999 !important;

      @media (max-width: $max-width-phone) {
        // Ensure PDF modal can scroll independently when nested in blog/event popover
        .w-full {
          overflow-y: auto !important;
          overflow-x: hidden !important;
          -webkit-overflow-scrolling: touch !important;
          touch-action: pan-y !important;
          pointer-events: auto !important;
          height: 100vh !important;
          max-height: 100vh !important;
        }
      }
    }

    // Mobile styles
    @media (max-width: $max-width-phone) {
      width: 100vw !important;
      max-width: 100vw !important;
      height: 100vh !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      transform: none !important;
      position: fixed !important;
      margin: 0 !important;
      padding: 0 !important;
      transition: none !important;
      background: transparent !important;
    }

    // Hide header for blog popover
    &.blog-popover .content-box > header {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      height: 0 !important;
      min-height: 0 !important;
      max-height: 0 !important;
      padding: 0 !important;
      margin: 0 !important;
      overflow: hidden !important;
      position: absolute !important;
      top: -9999px !important;
      left: -9999px !important;
      width: 0 !important;
    }

    // Remove padding-top for blog popover since header is hidden
    &.blog-popover .content-box {
      @media (max-width: $max-width-phone) {
        padding-top: $small-padding !important;
        padding-left: $small-padding !important;
        padding-right: $small-padding !important;
        padding-bottom: $small-padding !important;
      }
    }

    // Desktop styles
    @include wider-then-phone {
      width: $max-width-text-block + $base-size + $max-width-sidebar;
      max-width: calc(100vw - #{$base-padding} * 2);
      height: auto;
      top: 50%;
      left: 50%;
      right: auto;
      bottom: auto;
      transform: translate(-50%, -50%);
      margin: auto;
      padding: 0;
      position: fixed;
    }

    .modal-shadow {
      position: fixed !important;
      z-index: 0 !important;
      pointer-events: auto !important;
    }

    .content-box {
      position: relative !important;
      z-index: 1 !important;

      // Mobile styles
      @media (max-width: $max-width-phone) {
        height: 100vh !important;
        max-height: 100vh !important;
        width: 100% !important;
        max-width: 100% !important;
        padding: 0 !important;
        padding-top: calc(2.5rem + #{$small-padding} * 2) !important;
        padding-left: $small-padding !important;
        padding-right: $small-padding !important;
        padding-bottom: $small-padding !important;
        // Ensure content starts at the top
        scroll-padding-top: 0 !important;
        box-sizing: border-box !important;
        overflow-y: auto !important;
        overflow-x: hidden !important;
        -webkit-overflow-scrolling: touch !important;
        scroll-behavior: smooth !important;
        touch-action: pan-y !important;
        overscroll-behavior: contain !important;
        display: block !important;
        position: relative !important;
        top: 0 !important;
        left: 0 !important;
        right: auto !important;
        bottom: auto !important;
        transform: none !important;
        margin: 0 !important;
        z-index: 1 !important;
        transition: none !important;
        will-change: scroll-position !important;
        // Ensure content can overflow and scroll
        min-height: 0 !important;
        // Ensure content can expand beyond viewport for scrolling
        overflow: auto !important;
        overflow-y: auto !important;
        overflow-x: hidden !important;
        // Ensure scrolling works on mobile
        -webkit-overflow-scrolling: touch !important;
        touch-action: pan-y !important;
        pointer-events: auto !important;

        // Ensure ContentWithSidebar inside modal allows parent to scroll
        // Don't create nested scrolling - let content-box handle all scrolling
        .content-with-sidebar {
          height: auto !important;
          min-height: auto !important;
          max-height: none !important;
          overflow: visible !important;
          overflow-y: visible !important;
          overflow-x: hidden !important;
          display: block !important;
          flex: none !important;
          padding: 0 !important;
          -webkit-overflow-scrolling: auto !important;
          touch-action: auto !important;
          position: relative !important;
          width: 100% !important;
          visibility: visible !important;
          opacity: 1 !important;
          // Override the base component's height: 100% which can cause collapse
          height: auto !important;
          // Ensure it takes up space and expands to fit content
          flex-shrink: 0 !important;
          flex-grow: 0 !important;
          // Force expansion to fit content
          align-self: flex-start !important;
        }

        // Blog-specific fixes
        &.blog-popover .content-with-sidebar {
          height: auto !important;
          min-height: auto !important;
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
        }

        &.blog-popover .content-with-sidebar .main-box {
          height: auto !important;
          min-height: auto !important;
          visibility: visible !important;
          opacity: 1 !important;
        }

        // Event-specific fixes (events also use content-with-sidebar)
        .content-with-sidebar.event {
          height: auto !important;
          min-height: auto !important;
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
        }

        .content-with-sidebar.event .main-box {
          height: auto !important;
          min-height: auto !important;
          visibility: visible !important;
          opacity: 1 !important;
        }

        .content-with-sidebar.event .sidebar-box {
          height: auto !important;
          min-height: auto !important;
          visibility: visible !important;
          opacity: 1 !important;
        }

        .content-with-sidebar .main-box {
          overflow: visible !important;
          overflow-y: visible !important;
          overflow-x: hidden !important;
          height: auto !important;
          min-height: auto !important;
          max-height: none !important;
          position: relative !important;
          width: 100% !important;
          max-width: 100% !important;
          visibility: visible !important;
          opacity: 1 !important;
          // Force expansion to fit content
          flex-shrink: 0 !important;
          flex-grow: 0 !important;
          align-self: flex-start !important;
        }

        .content-with-sidebar .sidebar-box {
          overflow: visible !important;
          overflow-y: visible !important;
          overflow-x: hidden !important;
          height: auto !important;
          min-height: 0 !important;
          max-height: none !important;
          flex: none !important;
          padding: 0 !important;
          -webkit-overflow-scrolling: auto !important;
          touch-action: auto !important;
          position: relative !important;
        }
      }

      // Desktop styles
      @include wider-then-phone {
        height: calc(100vh - (#{$base-padding} - #{$border-thick-w}) * 2);
        max-height: calc(100vh - (#{$base-padding} - #{$border-thick-w}) * 2);
        padding: $base-padding;
        overflow-y: auto !important;
        overflow-x: hidden !important;
        display: block;
        -webkit-overflow-scrolling: touch;
        scroll-behavior: smooth;
      }
      // border: $border-thick-w solid #888888;

      & > header {
        position: absolute;
        top: 0;
        right: 0;
        left: 0;
        z-index: 1000;
        background: white;
        padding: $small-padding;
        box-sizing: border-box;
        height: 2.5rem;
        display: flex;
        align-items: center;
        min-height: 2.5rem;
        max-height: 2.5rem;

        @media (max-width: $max-width-phone) {
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          width: 100% !important;
          height: 2.5rem !important;
          min-height: 2.5rem !important;
          max-height: 2.5rem !important;
          padding: $small-padding !important;
          box-sizing: border-box !important;
          display: flex !important;
          align-items: center !important;
        }

        button {
          border-radius: 50%;
          background: white;

          @media (max-width: $max-width-phone) {
            width: 2rem;
            height: 2rem;
            min-width: 2rem;
            min-height: 2rem;

            i {
              font-size: 1.2rem;
            }
          }
        }
      }
    }
    .modal-shadow {
      content: '';
      position: fixed;
      height: calc(100vh + 10px);
      width: calc(100vw + 10px);
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      background: rgba(215, 215, 215, 0.8);
      z-index: 0;

      @media (max-width: $max-width-phone) {
        width: 100vw !important;
        height: 100vh !important;
        left: 0 !important;
        top: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        transform: none !important;
        z-index: 0 !important;
        background: rgba(215, 215, 215, 0.8) !important;
        position: fixed !important;
      }
    }
  }

  /* Disable fade transition on mobile to prevent size flash */
  @media (max-width: $max-width-phone) {
    .fade-enter-active,
    .fade-leave-active {
      transition: opacity 0s !important;
    }
  }
</style>
